/**
 * Price vs. nutrition scatter (spec section 17).
 *
 *   X = price, Y = protein (or calories)
 *
 * The point of this chart is that high-value products sit up and to the left.
 * An iso-value guide line through the best performer makes that readable rather
 * than something the user has to infer from position alone.
 *
 * A single series, so no legend; the top few points are directly labelled
 * instead of every point carrying a number.
 */

import { useId, useMemo, useState } from 'react';
import type { AnalysedProduct } from '@/lib/nutrition/ranking';
import { formatCurrency } from '@/lib/format';
import { trimNumber } from '@/lib/nutrition/units';

export type ScatterAxis = 'protein' | 'kcal';

interface Props {
  rows: AnalysedProduct[];
  axis: ScatterAxis;
  height?: number;
}

const PAD = { top: 14, right: 14, bottom: 30, left: 48 };

export function ValueScatterChart({ rows, axis, height = 220 }: Props): React.JSX.Element {
  const [hover, setHover] = useState<string | null>(null);
  const titleId = useId();
  const width = 520;

  const data = useMemo(() => {
    // Only products with BOTH coordinates can be plotted. Anything missing is
    // reported in the caption rather than silently dropped.
    const usable = rows
      .map((row) => {
        const price = row.metrics.values.price.value;
        const yValue =
          axis === 'protein'
            ? (row.metrics.perPackage?.nutrients.protein ?? null)
            : (row.metrics.perPackage?.nutrients.kcal ?? null);
        return price !== null && yValue !== null ? { row, price, yValue } : null;
      })
      .filter((d): d is { row: AnalysedProduct; price: number; yValue: number } => d !== null);

    if (usable.length === 0) return null;

    const prices = usable.map((d) => d.price);
    const ys = usable.map((d) => d.yValue);
    const xMax = Math.max(...prices) * 1.1;
    const yMax = Math.max(...ys) * 1.12;

    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;
    const x = (v: number): number => PAD.left + (xMax === 0 ? 0 : (v / xMax) * plotW);
    const y = (v: number): number => PAD.top + plotH - (yMax === 0 ? 0 : (v / yMax) * plotH);

    const points = usable.map((d) => ({
      ...d,
      cx: x(d.price),
      cy: y(d.yValue),
      ratio: d.yValue / d.price,
    }));

    // The best ratio defines the iso-value line: everything on it is equally
    // good value; everything below it is worse.
    const best = points.reduce((a, b) => (b.ratio > a.ratio ? b : a));

    return {
      points,
      best,
      x,
      y,
      xMax,
      yMax,
      plotH,
      plotW,
      excluded: rows.length - usable.length,
    };
  }, [rows, axis, height]);

  if (data === null) {
    return (
      <figure className="chart-figure">
        <p className="chart-empty">
          Not enough products have both a price and {axis === 'protein' ? 'protein' : 'calorie'} data
          to plot.
        </p>
      </figure>
    );
  }

  const yLabel = axis === 'protein' ? 'Protein per package (g)' : 'Energy per package (kcal)';
  const currency = data.points[0]?.row.metrics.currency ?? null;
  const active = hover === null ? null : data.points.find((p) => p.row.product.id === hover);

  // Directly label the three best-value points; the rest are hover-only.
  const labelled = new Set(
    [...data.points].sort((a, b) => b.ratio - a.ratio).slice(0, 3).map((p) => p.row.product.id),
  );

  return (
    <figure className="chart-figure">
      <div className="chart-wrap">
        <svg
          className="chart-svg"
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-labelledby={titleId}
          onMouseLeave={() => setHover(null)}
        >
          <title id={titleId}>
            Scatter plot of price against {yLabel.toLowerCase()}. Products higher and further left
            are better value. Best value: {data.best.row.product.name}.
          </title>

          {[0, 0.5, 1].map((f) => {
            const yy = PAD.top + data.plotH * (1 - f);
            return (
              <g key={f}>
                <line className="chart-grid-line" x1={PAD.left} x2={width - PAD.right} y1={yy} y2={yy} />
                <text className="chart-axis-label" x={PAD.left - 6} y={yy + 3} textAnchor="end">
                  {trimNumber(data.yMax * f, 0)}
                </text>
              </g>
            );
          })}

          {/* Iso-value line: same nutrition-per-dollar as the best performer. */}
          <line
            className="chart-crosshair"
            x1={PAD.left}
            y1={PAD.top + data.plotH}
            x2={data.x(data.xMax)}
            y2={data.y(Math.min(data.best.ratio * data.xMax, data.yMax))}
          />
          <text
            className="chart-label"
            x={width - PAD.right}
            y={PAD.top + 10}
            textAnchor="end"
            fill="var(--text-muted)"
          >
            best value/$ line
          </text>

          {data.points.map((p) => {
            const isActive = active?.row.product.id === p.row.product.id;
            return (
              <g key={p.row.product.id}>
                <circle
                  cx={p.cx}
                  cy={p.cy}
                  r={isActive ? 7 : 5}
                  fill={p.row.product.id === data.best.row.product.id ? 'var(--accent)' : 'var(--series-protein)'}
                  opacity={p.row.product.id === data.best.row.product.id ? 1 : 0.72}
                  /* 2px surface ring keeps overlapping marks separable. */
                  stroke="var(--surface-1)"
                  strokeWidth={2}
                  tabIndex={0}
                  role="button"
                  aria-label={`${p.row.product.name}: ${formatCurrency(p.price, currency)}, ${trimNumber(p.yValue, 0)} ${axis === 'protein' ? 'g protein' : 'kcal'}`}
                  onMouseEnter={() => setHover(p.row.product.id)}
                  onFocus={() => setHover(p.row.product.id)}
                  onBlur={() => setHover(null)}
                  style={{ cursor: 'pointer' }}
                />
                {labelled.has(p.row.product.id) && !isActive && (
                  <text className="chart-label" x={p.cx + 8} y={p.cy - 6}>
                    {truncate(p.row.product.name, 18)}
                  </text>
                )}
              </g>
            );
          })}

          <text className="chart-axis-label" x={PAD.left} y={height - 14}>
            {formatCurrency(0, currency)}
          </text>
          <text className="chart-axis-label" x={width - PAD.right} y={height - 14} textAnchor="end">
            {formatCurrency(data.xMax, currency)}
          </text>
          <text
            className="chart-axis-label"
            x={PAD.left + data.plotW / 2}
            y={height - 2}
            textAnchor="middle"
          >
            Price →
          </text>
        </svg>

        {active && (
          <div
            className="chart-tooltip"
            style={{
              left: `${(active.cx / width) * 100}%`,
              top: `${(active.cy / height) * 100}%`,
              transform: active.cx > width * 0.6 ? 'translate(-105%, -110%)' : 'translate(8%, -110%)',
            }}
          >
            <strong>{active.row.product.name}</strong>
            <br />
            {formatCurrency(active.price, currency)} ·{' '}
            {trimNumber(active.yValue, 0)} {axis === 'protein' ? 'g protein' : 'kcal'}
            <br />
            {trimNumber(active.ratio, 1)} {axis === 'protein' ? 'g' : 'kcal'} per{' '}
            {currency ?? 'unit'}
          </div>
        )}
      </div>
      <figcaption className="chart-caption">
        {yLabel} against price. Higher and further left is better value.
        {data.excluded > 0 &&
          ` ${data.excluded} product${data.excluded === 1 ? '' : 's'} omitted for missing data.`}
      </figcaption>
    </figure>
  );
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}
